import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useUser } from "../../context/UserContext";
import { convertFilesToBase64 } from "../../utils/fileUtils";
import { encryptForTransit, decryptFromTransit } from "../../utils/common";
import AmenitiesSelect from "./AmenitiesSelect";
import DeveloperProjectBoqTable from "../DeveloperProjectBoqTable";
import { IconButton, OutlineButton, PrimaryButton } from "../Buttons";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  FileTextIcon,
  Info,
  Loader2,
  X,
  Building2,
  FileText,
  Settings,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import CustomInput from "../CustomInput";
import { snakeToSentence } from "../../utils/helpers";
import { Tooltip } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { Field, AddressFields, yupResolver } from "../form";
import { projectDetailsSchema } from "../../services/projects/schemas";
import { searchCategories } from "../../services/materialAdmin";
import { toast } from "react-toastify";

// Shape of a single phase entry in the RHF `phases` field array.
// `images`/`reraBoqDocument`/`projectPhaseId`/`id` pass through from API
// hydration (read-only for this form). `boqItems` is a nested field array.
const emptyPhase = () => ({
  name: "",
  phaseReraNo: "",
  reraStartDate: "",
  phaseReraEndDate: "",
  phaseTowers: "",
  phaseUnits: "",
  phaseStatus: 1,
  images: [],
  reraBoqDocument: null,
  projectPhaseId: null,
  id: null,
  boqItems: [],
});

const initialFormData = {
  projectName: "",
  developer: "",
  streetName1: "",
  streetName2: "",
  buildingNo: "",
  city: "",
  district: "",
  state: "",
  pinCode: "",
  country: "",
  reraNo: "",
  projectTowers: "",
  sizeAcres: "",
  projectUnits: "",
  phoneNumber: "",
  whatsappNumber: "",
  specifications: "",
  location: "",
  bankDetails: {
    bankName: "",
    bankAccount: "",
    accountNo: "",
    branch: "",
    ifscCode: "",
  },
  costSheetTerms: "",
  amenities: [],
  // Phases live in RHF now (useFieldArray below) — must have at least one
  // entry by default so the empty form shows a phase to fill in.
  phases: [emptyPhase()],
};

const ProjectDetailsForm = ({
  projectDeatilaForm,
  onFormSave,
  onNext,
  setProjectId,
  isPostSubmitting,
  }) => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  // `methods` is preserved as a whole so we can hand it to <FormProvider>
  // below (Field reads RHF state via useFormContext under that provider).
  // The destructured helpers also keep working for the rest of the form
  // body — phases array, file uploads, etc. — that still register manually.
  const methods = useForm({
    defaultValues: initialFormData,
    mode: "onTouched",
    resolver: yupResolver(projectDetailsSchema),
  });
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
    trigger,
    getValues,
  } = methods;
  // The dynamic phase list lives in RHF state now. `append` / `remove` /
  // `fields` drive the JSX; `fields[i].id` gives us a stable key for React
  // without coupling to whatever id the data happens to carry.
  const {
    fields: phaseFields,
    append: appendPhase,
    remove: removePhaseAt,
  } = useFieldArray({ control, name: "phases" });
  
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [marketingMedia, setMarketingMedia] = useState([]);
  const [previewMarketingMedia, setPreviewMarketingMedia] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [specifications, setSpecifications] = useState("");
  // (phases moved to RHF — see useFieldArray('phases') below)
  const [materialCategories, setMaterialCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState(new Set([0])); // First phase expanded by default

  const tabs = [
    {
      id: 0,
      name: "Project Details",
      icon: <Building2 className="w-5 h-5" />,
      description: "Basic project information & address"
    },
    {
      id: 1,
      name: "Phase Details",
      icon: <FileText className="w-5 h-5" />,
      description: "Specifications, amenities & phase details"
    },
    {
      id: 2,
      name: "Documents",
      icon: <Settings className="w-5 h-5" />,
      description: "Bank details & file uploads"
    }
  ];

  const parseBankDetails = (bankDetailsString) => {
    if (!bankDetailsString)
      return {
        bankName: "",
        bankAccount: "",
        accountNo: "",
        branch: "",
        ifscCode: "",
      };
    const parts = bankDetailsString.split(",").map((part) => part.trim());

    let bankDetails = {
      bankName: "",
      bankAccount: "",
      accountNo: "",
      branch: "",
      ifscCode: "",
    };

    parts.forEach((part) => {
      if (part.startsWith("Bank Name:")) {
        bankDetails.bankName = part.split(":")[1].trim();
      } else if (part.startsWith("Bank Account:")) {
        bankDetails.bankAccount = part.split(":")[1].trim();
      } else if (part.startsWith("Account No:")) {
        bankDetails.accountNo = part.split(":")[1].trim();
      } else if (part.startsWith("Branch:")) {
        bankDetails.branch = part.split(":")[1].trim();
      } else if (part.startsWith("IFSC Code:")) {
        bankDetails.ifscCode = part.split(":")[1].trim();
      }
    });
    return bankDetails;
  };

  useEffect(() => {
    if (projectDeatilaForm) {
      // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
      let data = null;
      if (projectDeatilaForm?.items && Array.isArray(projectDeatilaForm.items) && projectDeatilaForm.items.length > 0) {
        // Paginated response - extract first item
        data = projectDeatilaForm.items[0];
      } else if (Array.isArray(projectDeatilaForm) && projectDeatilaForm.length > 0) {
        // Array response - get first item
        data = projectDeatilaForm[0];
      } else if (projectDeatilaForm && !projectDeatilaForm.items) {
        // Direct object response
        data = projectDeatilaForm;
      }
      
      if (!data) return;

      // Handle both snake_case and camelCase response formats
      const bankDetailsStr = decryptFromTransit(data.bankDetails || data.bank_details);
      const parsedBankDetails = bankDetailsStr
        ? parseBankDetails(bankDetailsStr)
        : {
          bankName: "",
          bankAccount: "",
          accountNo: "",
          branch: "",
          ifscCode: "",
        };
      
      // Initialize phases for RHF state — handle both snake_case and camelCase.
      // BE may return phases as `data.phases`; we always ensure at least one
      // entry so the empty form shows a phase to fill in.
      const initialPhases =
        Array.isArray(data.phases) && data.phases.length > 0
          ? data.phases.map((phase) => ({
              name: phase.name || "",
              phaseReraNo: phase.reraNo || phase.rera_no || "",
              reraStartDate: phase.reraStartDate || phase.rera_start_date || "",
              phaseReraEndDate:
                phase.reraEndDate || phase.rera_end_date || phase.phaseReraEndDate || "",
              phaseTowers: phase.phaseTowers || phase.phase_towers || phase.towers || "",
              phaseUnits: phase.phaseUnits || phase.phase_units || phase.units || "",
              phaseStatus: phase.phaseStatus || phase.phase_status || 1,
              images: phase.images || [],
              reraBoqDocument: phase.reraBoqDocument || phase.rera_boq_document || null,
              projectPhaseId:
                phase.projectPhaseId ?? phase.project_phase_id ?? phase.id ?? null,
              id: phase.id ?? phase.projectPhaseId ?? phase.project_phase_id ?? null,
              boqItems: [],
            }))
          : [emptyPhase()];

      reset({
        projectName: data.projectName || data.project_name || "",
        developer: data.developer || "",
        streetName1: data.address?.streetName1 || data.address_street_name1 || "",
        streetName2: data.address?.streetName2 || data.address_street_name2 || "",
        buildingNo: data.address?.buildingNo || data.address_building_no || "",
        city: data.address?.city || data.address_city || "",
        district: data.address?.district || data.address_district || "",
        state: data.address?.state || data.address_state || "",
        pinCode: data.address?.pinCode || data.address_pin_code || "",
        country: data.address?.country || data.address_country || "",
        projectTowers: data.projectTowers || data.project_towers || data.towers || "",
        projectUnits: data.projectUnits || data.project_units || data.units || "",
        phoneNumber: data.phoneNumber || data.phone_number || "",
        whatsappNumber: data.whatsappNumber || data.whatsapp_number || "",
        specifications: data.specifications || "",
        location: data.location || "",
        bankDetails: parsedBankDetails,
        costSheetTerms: data.costSheetTerms || data.cost_sheet_terms || "",
        amenities: Array.isArray(data.amenities) ? data.amenities : (data.amenities ? data.amenities.split(',') : []),
        phases: initialPhases,
      });
      setExpandedPhases(new Set([0]));

      if (data.images && data.images.length > 0) {
        setImages(data.images);
        setPreviewImages(data.images);
      }
      if (data.agreement) {
        setAgreement(data.agreement);
      }
    }
  }, [projectDeatilaForm, reset]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCategories(true);
    searchCategories({ isActive: true, pageSize: 500, withAttributes: false })
      .then((res) => {
        if (!cancelled) setMaterialCategories(res?.items || (Array.isArray(res) ? res : []));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error loading material categories:", err);
          toast.error("Failed to load material categories");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCategories(false);
      });
    return () => { cancelled = true; };
  }, []);

  const getCategoryId = (cat) =>
    cat?.material_category_id ?? cat?.materialCategoryId ?? cat?.id ?? "";
  const getCategoryName = (cat) =>
    cat?.category_name ?? cat?.categoryName ?? cat?.name ?? "";

  // Per-phase BOQ row handlers moved into the <PhaseRow> subcomponent below
  // — each phase row instantiates its own nested useFieldArray on
  // `phases.${index}.boqItems`.

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const base64Images = await convertFilesToBase64(files);
    setImages((prev) => [...prev, ...base64Images]);
    setPreviewImages((prev) => [...prev, ...base64Images]);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    const newPreviews = [...previewImages];
    URL.revokeObjectURL(previewImages[index]);
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setImages(newImages);
    setPreviewImages(newPreviews);
  };

  const handleAgreementChange = async (event) => {
    const file = event.target.files[0];
    try {
      const base64File = await convertFilesToBase64([file]);
      setAgreement(base64File[0]);
    } catch (error) {
      console.error("Error converting file to Base64:", error);
    }
  };

  const handleAgreementRemove = () => {
    setAgreement(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleMarketingMediaChange = async (e) => {
    const files = Array.from(e.target.files);
    const base64Media = await convertFilesToBase64(files);
    setMarketingMedia((prev) => [...prev, ...base64Media]);
    setPreviewMarketingMedia((prev) => [...prev, ...base64Media]);
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return null; // Will show image preview
    } else if (fileType === 'application/pdf') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
               fileType === 'application/msword') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
  };

  const renderPreview = (media) => {
    if (media.type.startsWith('image/')) {
      return (
        <img
          src={media.base64}
          alt={`Marketing Media`}
          className="w-28 h-28 object-cover rounded-md"
        />
      );
    } else {
      return (
        <div className="w-28 h-28 flex flex-col items-center justify-center bg-gray-50 rounded-md border border-gray-200">
          {getFileIcon(media.type)}
          <span className="text-xs text-gray-500 mt-1 truncate w-24 text-center">
            {media.name}
          </span>
        </div>
      );
    }
  };

  const removeMarketingMedia = (index) => {
    const newMedia = [...marketingMedia];
    const newPreviews = [...previewMarketingMedia];
    URL.revokeObjectURL(previewMarketingMedia[index]);
    newMedia.splice(index, 1);
    newPreviews.splice(index, 1);
    setMarketingMedia(newMedia);
    setPreviewMarketingMedia(newPreviews);
  };

  const handleSubmitForm = async (data) => {
    if (!isValid) return;

    // Frontend hard checks for required fields (covers non-react-hook-form state too).
    if (!data.developer?.trim()) {
      toast.error("Developer Name is required");
      setActiveTab(0);
      return;
    }
    if (!data.projectName?.trim()) {
      toast.error("Project Name is required");
      setActiveTab(0);
      return;
    }
    if (!data.projectTowers || Number(data.projectTowers) <= 0) {
      toast.error("Number of Towers is required");
      setActiveTab(0);
      return;
    }
    if (!data.sizeAcres || Number(data.sizeAcres) <= 0) {
      toast.error("Size (acres) is required");
      setActiveTab(0);
      return;
    }
    if (!data.buildingNo?.trim() || !data.streetName1?.trim() || !data.city?.trim() || !data.district?.trim() || !data.state?.trim() || !data.pinCode?.trim() || !data.country?.trim()) {
      toast.error("Please complete all required Address fields");
      setActiveTab(0);
      return;
    }

    // Validate phases — read straight from RHF state (data.phases) instead
    // of the old useState. Same rules: at least one phase, each phase needs
    // name/towers/units. Yup could enforce some of this declaratively later;
    // the imperative path keeps existing UX (jump to tab 1 on first failure).
    const phasesData = data.phases || [];
    if (phasesData.length === 0) {
      toast.error("Please add at least one phase");
      setActiveTab(1);
      return;
    }
    for (let i = 0; i < phasesData.length; i++) {
      const phase = phasesData[i];
      if (!phase.name?.trim() || !phase.phaseTowers || Number(phase.phaseTowers) <= 0 || !phase.phaseUnits || Number(phase.phaseUnits) <= 0) {
        toast.error(`Phase ${i + 1}: Name, Towers, and Units are required`);
        setActiveTab(1);
        return;
      }
    }

    if (!agreement) {
      toast.error("Agreement Template is required");
      setActiveTab(2);
      return;
    }
    if (!images || images.length === 0) {
      toast.error("At least one Project Image is required");
      setActiveTab(2);
      return;
    }
    
    setIsSubmitting(true);
    const amenitiesString = amenities.join(",");
    const bankDetailsString = `Bank Name: ${data.bankDetails.bankName},\nBank Account: ${data.bankDetails.bankAccount},\nAccount No: ${data.bankDetails.accountNo},\nBranch: ${data.bankDetails.branch},\nIFSC Code: ${data.bankDetails.ifscCode}`;

    console.log("Phases state before mapping:", phasesData);

    const formDataToSend = {
      // Project details
      project_name: data.projectName,
      developer: data.developer,
      customer_id: userInfo?.loginIdentifierDetails?.customer?.customerId || 0,
      address_building_no: data.buildingNo,
      address_street_name1: data.streetName1,
      address_street_name2: data.streetName2,
      address_city: data.city,
      address_district: data.district,
      address_pin_code: data.pinCode,
      address_state: data.state,
      address_country: data.country,
      amenities: amenitiesString,
      project_towers: parseInt(data.projectTowers),
      project_units: parseInt(data.projectUnits),
      phone_number: data.phoneNumber || "",
      whatsapp_number: data.whatsappNumber || "",
      specifications: specifications,
      location: data.location,
      size_acres: parseFloat(data.sizeAcres),
      bank_details: encryptForTransit(bankDetailsString),
      marketing_media: marketingMedia,
      images: images,
      agreement: agreement, // Include agreement data
      
      // Phase details — read from RHF state (`data.phases`). Same snake_case
      // wire shape as before so the parent / BE contract is unchanged.
      phases: phasesData.map((phase) => ({
        name: phase.name || "",
        rera_no: phase.phaseReraNo || "",
        rera_start_date: phase.reraStartDate || "",
        rera_end_date: phase.phaseReraEndDate || "",
        phase_towers: phase.phaseTowers ? parseInt(phase.phaseTowers) : 0,
        phase_units: phase.phaseUnits ? parseInt(phase.phaseUnits) : 0,
        phase_status: phase.phaseStatus || 1,
        images: phase.images || [],
        rera_boq_document: phase.reraBoqDocument || null,
        boqItems: (phase.boqItems || [])
          .map((item) => ({
            materialCategoryId: parseInt(item.materialCategoryId, 10) || 0,
            details: String(item.details ?? "").trim(),
          }))
          .filter((item) => item.materialCategoryId > 0),
      })),
    };
   
    console.log("Form Data Being Sent:", formDataToSend);

    try {
     
      onFormSave(formDataToSend);
      onNext();
    } catch (error) {
      if (error.response?.status === 422) {
        toast.error("Invalid or missing required fields. Please check your input.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  const handleSpecificationsChange = (value) => {
    setSpecifications(value);
  };

  // Phase add/remove now goes through useFieldArray. Expansion state stays in
  // useState because it's UI-only (no need to live in RHF).
  const addPhase = () => {
    appendPhase(emptyPhase());
    setExpandedPhases((prev) => new Set([...prev, phaseFields.length]));
  };

  const togglePhaseExpansion = (index) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const removePhase = (index) => {
    removePhaseAt(index);
    // Keep expansion indices in sync after the splice.
    setExpandedPhases((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  // Which RHF field names belong to each tab. Used by nextTab() to gate
  // navigation — you can't advance until the current tab's required fields
  // are valid. Tab 1's per-phase fields are checked imperatively because
  // useFieldArray paths depend on the runtime row count.
  const tabFieldNames = {
    0: [
      "developer", "projectName", "projectTowers", "projectUnits",
      "phoneNumber", "whatsappNumber", "location", "sizeAcres",
      "buildingNo", "streetName1", "streetName2",
      "city", "district", "state", "pinCode", "country",
    ],
    2: [
      "bankDetails.bankName", "bankDetails.bankAccount",
      "bankDetails.accountNo", "bankDetails.branch", "bankDetails.ifscCode",
    ],
  };

  const validateCurrentTab = async () => {
    if (activeTab === 1) {
      // Phases are dynamic — validate each row's required fields by path.
      const phases = getValues("phases") || [];
      if (phases.length === 0) {
        toast.error("Please add at least one phase");
        return false;
      }
      const paths = [];
      phases.forEach((_, i) => {
        paths.push(`phases.${i}.name`);
        paths.push(`phases.${i}.phaseTowers`);
        paths.push(`phases.${i}.phaseUnits`);
      });
      const ok = await trigger(paths);
      if (!ok) {
        // Find which phase failed and surface a clear message.
        for (let i = 0; i < phases.length; i++) {
          const p = phases[i];
          if (!p.name?.trim() || !p.phaseTowers || Number(p.phaseTowers) <= 0
              || !p.phaseUnits || Number(p.phaseUnits) <= 0) {
            toast.error(`Phase ${i + 1}: Name, Towers, and Units are required`);
            return false;
          }
        }
        return false;
      }
      return true;
    }
    const fields = tabFieldNames[activeTab] || [];
    return await trigger(fields);
  };

  const nextTab = async () => {
    if (activeTab >= tabs.length - 1) return;
    const ok = await validateCurrentTab();
    if (!ok) return;
    setActiveTab(activeTab + 1);
  };

  const prevTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Project Details & Address
        return (
          <div className="space-y-6 mb-16">
            {/* Project Details — pair fields in a clean 2-column grid for
                even rows (8 fields → 4 rows of 2). Same gap as AddressFields
                so the two sections line up vertically. */}
            <section>
              <h3 className="text-base font-semibold text-slate-900 mb-1">
                Basic Project Information
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Who's building it and where it is.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field name="developer" label="Developer Name" required />
                <Field name="projectName" label="Project Name" required />
                <Field name="projectTowers" label="Number of Towers" type="number" required />
                <Field name="projectUnits" label="Number of Units" type="number" />
                <Field name="sizeAcres" label="Size (acres)" type="number" required />
                <Field name="location" label="Location" />
                <Field name="phoneNumber" label="Phone Number" type="tel" />
                <Field name="whatsappNumber" label="WhatsApp Number" type="tel" />
              </div>
            </section>

            <section>
              <h3 className="text-base font-semibold text-slate-900 mb-1">Address</h3>
              <p className="text-xs text-slate-500 mb-4">
                Office or project site address — used on letterheads and documents.
              </p>
              {/* Project's address fields are FLAT (not nested) in form state;
                  pass prefix="" so AddressFields registers at the top level. */}
              <AddressFields
                prefix=""
                required={["buildingNo", "streetName1", "city", "district", "state", "pinCode", "country"]}
              />
            </section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Specifications */}
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <label className="text-lg font-medium text-gray-700">Specifications</label>
                      <Tooltip placement="top">
                        <Tooltip.Trigger>
                          <Info className="w-4 h-4 text-flamingo-500 cursor-help" />
                        </Tooltip.Trigger>
                        <Tooltip.Content className="bg-white text-gray-700 text-sm border border-gray-200 w-80 p-3 shadow-sm">
                          <div className="space-y-2">
                            <p className="font-medium text-flamingo-600">Example specifications:</p>
                            <ul className="text-xs space-y-1">
                              <li>• Earthquake-resistant R.C.C frame structure</li>
                              <li>• External walls 6'' thick with sand-faced plaster</li>
                              <li>• Internal 4'' thick brickwork walls with gypsum-finished plaster</li>
                              <li>• 2' X 2' vitrified flooring tiles for entire unit</li>
                              <li>• Anti-skid flooring for toilet & terraces</li>
                            </ul>
                          </div>
                          <Tooltip.Arrow />
                        </Tooltip.Content>
                      </Tooltip>
                    </div>
                    <div className="h-44">
                      <ReactQuill
                        value={specifications}
                        onChange={handleSpecificationsChange}
                        modules={quillModules}
                        className="h-full"
                        placeholder="Enter project specifications..."
                      />
                    </div>
                  </div>
                  {/* Amenities */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mt-4 mb-3 block">Amenities</label>
                    <AmenitiesSelect
                      amenities={amenities}
                      setAmenities={setAmenities}
                    />
                  </div>
                </div>

         
           
          </div>
        );

      case 1: // Project Info (Specifications, Amenities & Phase Details)
        return (
          <div className="space-y-2">
            {/* Combined Project Info Card */}
            <div className="p-2">
              
              <div className="space-y-8">

                {/* Phase Details */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-base font-medium text-gray-800">Phase Configuration</h4>
                    <button
                      type="button"
                      onClick={addPhase}
                      className="px-4 py-2 bg-flamingo-500 hover:bg-flamingo-600 text-white text-sm font-medium rounded transition-colors"
                    >
                      + New Phase
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    {phaseFields.map((field, index) => (
                      <PhaseRow
                        key={field.id}
                        index={index}
                        isExpanded={expandedPhases.has(index)}
                        onToggleExpand={() => togglePhaseExpansion(index)}
                        onRemove={() => removePhase(index)}
                        materialCategories={materialCategories}
                        loadingCategories={loadingCategories}
                        getCategoryId={getCategoryId}
                        getCategoryName={getCategoryName}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Documents (Bank Details & File Uploads)
        return (
          <div className="space-y-2">
            <div className="p-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details & Documents</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4">Bank Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field name="bankDetails.bankName" label="Bank Name" />
                    <Field name="bankDetails.bankAccount" label="Account Holder Name" />
                    <Field name="bankDetails.accountNo" label="Account Number" type="number" />
                    <Field name="bankDetails.branch" label="Branch" />
                    <Field name="bankDetails.ifscCode" label="IFSC Code" className="md:col-span-2" />
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-base font-medium text-gray-800 mb-4">Required Documents</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className=" border rounded-md border-gray-200 p-4">
                      <div className="flex justify-between items-center mb-3">
                        <label className="text-sm font-medium text-gray-700">
                          Agreement Template
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <OutlineButton
                          icon={<Download className="w-4 h-4 mr-2" />}
                          left
                          className="text-xs bg-flamingo-50 text-flamingo-700 hover:bg-flamingo-100 border-flamingo-200"
                          onClick={() =>
                            (window.location.href = "/Agreement_sample_testing.docx")
                          }
                        >
                          Template
                        </OutlineButton>
                      </div>
                      <div className="relative flex items-center justify-center w-full h-20 px-3 transition bg-white border border-gray-200 border-dashed appearance-none cursor-pointer hover:border-flamingo-300 focus:outline-none">
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleAgreementChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          required
                        />
                        <div className="text-center">
                          <FileTextIcon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                          <span className="text-sm text-gray-500">
                            Drop file here or browse
                          </span>
                        </div>
                      </div>
                      {agreement && (
                        <div className="mt-3 flex items-center space-x-2 bg-green-50 border border-green-200 p-2">
                          <FileTextIcon className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-700">
                            {snakeToSentence(agreement.name)}
                          </span>
                          <button
                            onClick={handleAgreementRemove}
                            className="text-red-500 hover:text-red-700 ml-auto"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className=" border rounded-md border-gray-200 p-4">
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Project Images
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative flex items-center justify-center w-full h-20 px-3 transition bg-white border border-gray-200 border-dashed appearance-none cursor-pointer hover:border-flamingo-300 focus:outline-none">
                        <input
                          type="file"
                          multiple
                          onChange={handleImageChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          required
                        />
                        <div className="text-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-6 h-6 text-gray-400 mx-auto mb-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-sm text-gray-500">
                            Drop images here or browse
                          </span>
                        </div>
                      </div>
                      {previewImages.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {previewImages.map((image, index) => (
                            <div key={index} className="relative">
                              <img
                                src={image.base64}
                                alt={`Preview ${index + 1}`}
                                className="w-16 h-16 object-cover border border-gray-200"
                              />
                              <button
                                onClick={() => removeImage(index)}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded flex items-center justify-center text-xs hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    // FormProvider wraps the whole component so <Field> reads RHF state via
    // useFormContext. Submission is still triggered via the existing
    // onClick={handleSubmit(handleSubmitForm)} pattern at the page level
    // (this form spans multiple tabs and has no enclosing <form> element).
    <FormProvider {...methods}>
    <div className="max-h-screen ">
      {/* Header */}
      {/* <div className=" px-4 py-4">
        <div className="flex items-center gap-4">
          <OutlineButton
            onClick={() => {
              reset(initialFormData);
              navigate("/projects");
            }}
            className="w-fit p-2 hover:bg-gray-100 transition-colors"
            icon={<ArrowLeft className="w-5 h-5" />}
          />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Create New Project</h1>
           
          </div>
        </div>
      </div> */}

      {/* Main Form Layout with Left Sidebar */}
      <div className="flex h-[calc(100vh-140px)]">
        {/* Left Sidebar — soft slate background separates the wizard nav from
            the form content on the right. */}
        <div className="w-80 flex-shrink-0 bg-slate-50 border-r border-slate-200">
          <div className="p-6 h-full">
            {/* Wizard header — slate gradient matches every other section
                header in the app (FormModal, Onboarding, RBAC). */}
            <div className="-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r from-slate-800 to-slate-600 text-white">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    reset(initialFormData);
                    navigate("/projects");
                  }}
                  className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Back to projects"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-base font-semibold">Create New Project</h1>
                  <p className="text-xs text-slate-300 mt-0.5">
                    Step {activeTab + 1} of {tabs.length} — {tabs[activeTab]?.name}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {tabs.map((tab, index) => {
                const isCurrent = activeTab === tab.id;
                const isDone = activeTab > index;
                // Going back to a previous tab is always allowed; jumping
                // forward must satisfy the same per-tab validation as Next.
                const goToTab = async () => {
                  if (tab.id <= activeTab) {
                    setActiveTab(tab.id);
                    return;
                  }
                  // For each tab between current and target, validate.
                  for (let i = activeTab; i < tab.id; i++) {
                    setActiveTab(i);
                    // eslint-disable-next-line no-await-in-loop
                    const ok = await validateCurrentTab();
                    if (!ok) return;
                  }
                  setActiveTab(tab.id);
                };
                return (
                  <div key={tab.id} className="relative">
                    <button
                      onClick={goToTab}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                        isCurrent
                          ? "bg-white shadow-sm text-flamingo-700 border-l-4 border-flamingo-500"
                          : isDone
                            ? "text-slate-700 hover:bg-white/60"
                            : "text-slate-500 hover:bg-white/60"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium shrink-0 ${
                          isCurrent
                            ? "bg-flamingo-500 text-white"
                            : isDone
                              ? "bg-green-500 text-white"
                              : "bg-slate-200 text-slate-600"
                        }`}>
                          {isDone ? (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            index + 1
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{tab.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">
                            {tab.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Progress Indicator */}
            {/* <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                <span className="font-medium">Progress</span>
                <span className="font-semibold text-flamingo-500">{Math.round(((activeTab + 1) / tabs.length) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-sm h-2">
                <div 
                  className="bg-flamingo-500 h-2 rounded-sm transition-all duration-300"
                  style={{ width: `${((activeTab + 1) / tabs.length) * 100}%` }}
                ></div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Right Side - Form Content */}
        <div className="flex-1 bg-white overflow-y-auto flex flex-col">
          <div className="h-full flex flex-col">
            <div className="flex-1 p-2">
              <div className="p-2 m-2">
                {renderTabContent()}
              </div>
            </div>

            {/* Sticky Navigation Buttons at Bottom */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-between items-center shadow-sm">
              <div className="flex gap-3">
                {/* <OutlineButton
                  onClick={(e) => {
                    e.preventDefault();
                    reset(initialFormData);
                  }}
                  className="px-6 py-2"
                >
                  Clear Form
                </OutlineButton> */}
                {activeTab > 0 && (
                  <OutlineButton
                    onClick={(e) => {
                      e.preventDefault();
                      prevTab();
                    }}
                    className="px-6 py-2"
                  >
                    Back
                  </OutlineButton>
                )}
              </div>

              <div className="flex gap-3">
                {activeTab < tabs.length - 1 ? (
                  <PrimaryButton
                    onClick={(e) => {
                      e.preventDefault();
                      nextTab();
                    }}
                    className="px-8 py-2 "
                  >
                    Next
                  </PrimaryButton>
                ) : (
                  <IconButton
                    className=""
                    left
                    icon={
                      isSubmitting || isPostSubmitting ? (
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="mr-2 w-4 h-4" />
                      )
                    }
                    onClick={handleSubmit(handleSubmitForm)}
                    disabled={isSubmitting || isPostSubmitting}
                  >
                    {isSubmitting || isPostSubmitting ? "Submitting..." : "Create"}
                  </IconButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </FormProvider>
  );
};

export default ProjectDetailsForm;

/**
 * Per-phase row inside Tab 1. Lives under the outer <FormProvider>, so all
 * its inputs (and the nested BOQ-rows useFieldArray) read/write RHF state at
 * the canonical paths: `phases.${index}.<field>` and
 * `phases.${index}.boqItems.${rowIndex}.<col>`.
 *
 * Existing phases (`projectPhaseId` / `id` set) show the DeveloperProjectBoqTable
 * widget against the saved phase; new phases show the inline draft-BOQ table
 * whose rows get included in the create-project payload.
 */
function PhaseRow({
  index,
  isExpanded,
  onToggleExpand,
  onRemove,
  materialCategories,
  loadingCategories,
  getCategoryId,
  getCategoryName,
}) {
  const { register, control, watch } = useFormContext();
  const phase = watch(`phases.${index}`) || {};
  const {
    fields: boqRows,
    append: appendBoqRow,
    remove: removeBoqRow,
  } = useFieldArray({ control, name: `phases.${index}.boqItems` });

  const isExistingPhase = phase.projectPhaseId != null || phase.id != null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-1">
          <button
            type="button"
            onClick={onToggleExpand}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>
          <h5 className="text-sm font-medium text-gray-700">
            Phase {index + 1} {phase.name && `- ${phase.name}`}
          </h5>
          {!isExpanded && (
            <span className="text-xs text-gray-500">
              ({phase.phaseTowers || 0} Towers, {phase.phaseUnits || 0} Units)
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 text-sm"
        >
          Remove Phase
        </button>
      </div>

      {isExpanded && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomInput label=" Name" required {...register(`phases.${index}.name`)} />
            <CustomInput label=" RERA Number" {...register(`phases.${index}.phaseReraNo`)} />
            <CustomInput
              label="RERA Start Date"
              type="date"
              {...register(`phases.${index}.reraStartDate`)}
            />
            <CustomInput
              label=" RERA End Date"
              type="date"
              {...register(`phases.${index}.phaseReraEndDate`)}
            />
            <CustomInput
              type="number"
              label=" Number of Towers"
              required
              {...register(`phases.${index}.phaseTowers`)}
            />
            <CustomInput
              type="number"
              label="Number of Units"
              required
              {...register(`phases.${index}.phaseUnits`)}
            />
          </div>

          {/* Developer Project BOQ — table widget for saved phases, draft
              rows (nested useFieldArray) for new ones. */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Developer Project BOQ (optional)
            </h4>
            {isExistingPhase ? (
              <DeveloperProjectBoqTable
                projectPhaseId={phase.projectPhaseId ?? phase.id}
                title=""
                showTitle={false}
                className="mt-2"
              />
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Add BOQ items for this phase. They will be saved when you create the project.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Material Category
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Details
                        </th>
                        <th className="px-3 py-2 w-20"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {boqRows.map((row, rowIndex) => (
                        <tr key={row.id}>
                          <td className="px-3 py-2">
                            <select
                              {...register(`phases.${index}.boqItems.${rowIndex}.materialCategoryId`)}
                              className="w-full min-w-[180px] rounded border border-gray-300 px-2 py-1.5 text-sm"
                              disabled={loadingCategories}
                            >
                              <option value="">Select category</option>
                              {materialCategories.map((cat) => (
                                <option key={getCategoryId(cat)} value={getCategoryId(cat)}>
                                  {getCategoryName(cat)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              {...register(`phases.${index}.boqItems.${rowIndex}.details`)}
                              placeholder="Details"
                              className="w-full min-w-[160px] rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => removeBoqRow(rowIndex)}
                              className="text-red-500 hover:text-red-700 p-1"
                              aria-label="Remove row"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => appendBoqRow({ materialCategoryId: "", details: "" })}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  + Add row
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
